import { HOAI_MAINTENANCE_LP8_MAX, HOAI_SERVICE_KEYS, HOAI_SERVICE_PROFILES } from './hoai-services';
/** All Leistungsbilder, Teil 2 to Teil 4. */
export const HOAI_SERVICES = HOAI_SERVICE_KEYS;
/** The profile of a Leistungsbild: table, basis, zones, phases, criteria, supplements. */
export function hoaiService(service) {
    const p = HOAI_SERVICE_PROFILES[service];
    if (!p)
        throw new Error(`Unbekanntes Leistungsbild ${service}`);
    return p;
}
/** Names of the services, for the interface and the annex alike. */
export const HOAI_SERVICE_NAMES = Object.fromEntries(HOAI_SERVICES.map((k) => [k, HOAI_SERVICE_PROFILES[k].name]));
/** The HOAI's part each Leistungsbild belongs to. */
export const HOAI_SERVICE_PARTS = Object.fromEntries(HOAI_SERVICES.map((k) => [k, HOAI_SERVICE_PROFILES[k].part]));
/** Share of each phase in percent of the fee, per Leistungsbild. */
export const HOAI_PHASES = Object.fromEntries(HOAI_SERVICES.map((k) => [k, HOAI_SERVICE_PROFILES[k].phases.map((ph) => ph.share)]));
export const HOAI_TABLES = Object.fromEntries(HOAI_SERVICES.map((k) => [k, HOAI_SERVICE_PROFILES[k].table]));
/** The zone a number of points gives; for a Leistungsbild without points the highest zone stays unchanged. */
export function zoneForPoints(service, points) {
    const p = hoaiService(service);
    const b = p.bounds ?? [];
    const i = b.findIndex((max) => points <= max);
    return (i < 0 ? p.zones : i + 1);
}
/** Eligible costs of a building after § 33: KG 300 plus KG 400 in part, when the office does not plan it. */
export function eligibleCostsBuilding(kg300, kg400, plansKg400) {
    if (plansKg400)
        return { eligible: kg300 + kg400, kg400Eligible: kg400 };
    const full = Math.min(kg400, 0.25 * kg300);
    const half = Math.max(0, kg400 - 0.25 * kg300) / 2;
    return { eligible: kg300 + full + half, kg400Eligible: full + half };
}
/** "von" and "bis" of a zone at a given basis (costs or hectares), interpolated linearly (§ 13); null outside the table. */
export function feeSpan(service, zone, basis) {
    const t = HOAI_TABLES[service];
    if (zone > hoaiService(service).zones)
        return null;
    if (!(basis >= t[0][0]) || basis > t[t.length - 1][0])
        return null;
    const lo = 1 + (zone - 1) * 2;
    const i = t.findIndex((r) => r[0] >= basis);
    if (t[i][0] === basis)
        return { low: t[i][lo], high: t[i][lo + 1] };
    const a = t[i - 1], b = t[i];
    const f = (basis - a[0]) / (b[0] - a[0]);
    const at = (k) => a[k] + f * (b[k] - a[k]);
    return { low: at(lo), high: at(lo + 1) };
}
const r2 = (x) => Math.round(x * 100) / 100;
export function calculateHoai(i) {
    const notes = [];
    const t = HOAI_TABLES[i.service];
    const span = feeSpan(i.service, i.zone, i.costs);
    const def = hoaiService(i.service);
    if (!span) {
        const unit = def.basis === 'costs' ? ' €' : def.basis === 'hectare' ? ' ha' : '';
        notes.push(i.zone > def.zones
            ? `${def.name} kennt nur die Honorarzonen I bis ${['', 'I', 'II', 'III', 'IV', 'V'][def.zones]}.`
            : `${def.basisLabel} außerhalb der Honorartafel (${t[0][0].toLocaleString('de-DE')} bis ${t[t.length - 1][0].toLocaleString('de-DE')}${unit}): Die HOAI nennt dafür keinen Wert, das Honorar ist frei zu vereinbaren.`);
        return { span: null, full: null, phases: [], basic: 0, conversion: 0, maintenance: 0, incidentals: 0, net: 0, vatAmount: 0, gross: 0, notes };
    }
    const rate = Math.min(100, Math.max(0, i.rate)) / 100;
    const full = span.low + rate * (span.high - span.low);
    const std = HOAI_PHASES[i.service];
    const phases = std.map((s, n) => ({ lp: n + 1, share: i.phases[n] ?? 0, standard: s }))
        .filter((p) => p.share > 0)
        .map((p) => {
        if (p.share > p.standard)
            notes.push(`LP ${p.lp}: ${p.share} % liegt über dem Anteil der HOAI (${p.standard} %).`);
        return { lp: p.lp, share: p.share, fee: r2((full * p.share) / 100) };
    });
    const basic = r2(phases.reduce((s, p) => s + p.fee, 0));
    // Supplements only where the HOAI provides them for the Leistungsbild.
    const conversion = def.maxConversion !== null ? r2((basic * i.conversion) / 100) : 0;
    if (def.maxConversion !== null && i.conversion > def.maxConversion)
        notes.push(`Umbauzuschlag ${i.conversion} % liegt über den ${def.maxConversion} %, die die HOAI bei durchschnittlichem Schwierigkeitsgrad nennt.`);
    const lp8 = def.maintenanceLp8 ? phases.find((p) => p.lp === 8) : undefined;
    const maintenance = lp8 ? r2((lp8.fee * Math.min(HOAI_MAINTENANCE_LP8_MAX, i.maintenanceLp8)) / 100) : 0;
    const incidentals = r2(((basic + conversion + maintenance) * i.incidentals) / 100);
    const net = r2(basic + conversion + maintenance + incidentals);
    const vatAmount = r2(net * i.vat);
    return { span: { low: r2(span.low), high: r2(span.high) }, full: r2(full), phases, basic, conversion, maintenance, incidentals, net, vatAmount, gross: r2(net + vatAmount), notes };
}
/** A start: all phases at the HOAI's share, Basishonorarsatz, no supplements. */
export function defaultHoaiInput(service = 'gebaeude') {
    return { service, zone: hoaiService(service).zones === 3 ? 2 : 3, costs: 0, rate: 0, phases: [...HOAI_PHASES[service]], conversion: 0, maintenanceLp8: 0, incidentals: 0, vat: 0.19 };
}
/** Phase names of the Objektplanung (Gebäude); other Leistungsbilder name theirs in their profile. */
export const HOAI_PHASE_NAMES = [
    'Grundlagenermittlung', 'Vorplanung', 'Entwurfsplanung', 'Genehmigungsplanung', 'Ausführungsplanung',
    'Vorbereitung der Vergabe', 'Mitwirkung bei der Vergabe', 'Objektüberwachung – Bauüberwachung und Dokumentation', 'Objektbetreuung',
];
//# sourceMappingURL=hoai.js.map